import { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { fetchInjuriesAndSuspensions, fetchLastFiveMatches, fetchLineups, fetchOdds, fetchSummary, getHeadToHead, } from "../network/FootbalService"
import { formatDate, formatTime } from "../utils/FormatDateTime"
import logoImage from '../images/logo192.png'
import { canAnalyzeMore, incrementAnalysisCount } from "../utils/LimitHelper"
import { useAuth } from "../hooks/AuthContext"
import FeedbackPopup from "../utils/FeedbackPopup"
import { logEvent } from "firebase/analytics"
import { analytics, db } from "../services/Firebase"
import { Button } from "react-bootstrap"
import { doc, serverTimestamp, setDoc } from "firebase/firestore"
import { useTranslation } from "react-i18next"
import Aurora from "../utils/Aurora"

export default function ResultPage() {
    const location = useLocation()
    const match = location.state

    const [homeTeamData, setHomeTeamData] = useState({})
    const [awayTeamData, setAwayTeamData] = useState({})
    const [playersNotPlaying, setPlayersNotPlaying] = useState({})
    const [lineups, setLineups] = useState({})
    const [headToHead, setHeadToHead] = useState([])
    const navigate = useNavigate()
    const { authUser } = useAuth()
    const [loadingSummaryButton, setLoadingSummaryButton] = useState(false)
    const [saveDisabled, setSaveDisabled] = useState(true)
    const { t, i18n } = useTranslation()
    const selectedLanguage = i18n.language === "sr" ? "Serbian" : "English";

    const [matchOdds, setMatchOdds] = useState({
        matchWinner: {},
        overUnder2_5: {}
    })
    const [summary, setSummary] = useState('')
    const [loading, setLoading] = useState(true)
    const [step, setStep] = useState(0);

    const loadingMessages = [
        t('fetchingStatistics'),
        t('analyzingTeams'),
        t('aiWorkingOnSummary'),
    ];

    const MATCH_WINNER_ID = 1
    const OVER_UNDER_ID = 5

    useEffect(() => {
        const interval = setInterval(() => {
            setStep((prev) => (prev + 1) % loadingMessages.length);
        }, 2000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (!match || !match.id) {
            navigate('/', { replace: true });
            return
        }

        if (!canAnalyzeMore(!!authUser)) {
            let alertMessage = t('dailyLimitReached');
            if (authUser !== null) {
                alertMessage = t('todayLimitUsed')
                // todo poslati analytic event za limit ulogovanog
            } else {
                // todo poslati analytic event za limit neulogovanog
            }
            alert(alertMessage);
            return;
        }

        const fetchMatchData = async () => {
            try {
                const [homeTeamForm, awayTeamForm, playersNotPlaying, odds, lineups, h2h] = await Promise.all([
                    fetchHomeMatches(match.teamHomeId),
                    fetchAwayMatches(match.teamAwayId),
                    fetchPlayerNotPlaying(match.id),
                    fetchOddsPrediction(match.id),
                    fetchTeamLineups(match.id),
                    fetchHeadToHead(match.teamHomeId, match.teamAwayId)
                ])
                setHomeTeamData(homeTeamForm)
                setAwayTeamData(awayTeamForm)
                setPlayersNotPlaying(playersNotPlaying)
                setLineups(lineups)
                setHeadToHead(h2h)
                console.log('h2h', h2h)

                await generateSummary(homeTeamForm, awayTeamForm, playersNotPlaying, odds, lineups, h2h)

            } catch (error) {
                console.log(error)
                logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            }
        }

        if (match?.id && match?.teamHomeId && match?.teamAwayId) {
            fetchMatchData();
            incrementAnalysisCount()
        }
    }, [match])

    async function fetchHomeMatches(teamId) {
        setLoading(true)
        try {
            const response = await fetchLastFiveMatches(teamId)
            const fixtures = response.data.response
            const last5MatchesForm = getLastMatchesForm(fixtures, teamId);
            const goals = calculateGoalAverages(fixtures, teamId)

            return {
                last5MatchesForm: last5MatchesForm,
                averageGoalsScored: goals.averageGoalsScored,
                averagesGoalsConceded: goals.averagesGoalsConceded
            }
        } catch (error) {
            console.log(error)
            setLoading(false)
            logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            return {}
        }
    }

    async function fetchAwayMatches(teamId) {
        setLoading(true)
        try {
            const response = await fetchLastFiveMatches(teamId)
            const fixtures = response.data.response
            const last5MatchesForm = getLastMatchesForm(fixtures, teamId);
            const goals = calculateGoalAverages(fixtures, teamId)

            return {
                last5MatchesForm: last5MatchesForm,
                averageGoalsScored: goals.averageGoalsScored,
                averagesGoalsConceded: goals.averagesGoalsConceded
            }

        } catch (error) {
            console.log(error)
            setLoading(false)
            logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            return {}
        }
    }

    async function fetchPlayerNotPlaying(matchId) {
        try {
            const response = await fetchInjuriesAndSuspensions(matchId)

            const playerByTeam = {
                home: [],
                away: []
            }

            response.data.response.forEach(p => {
                if (p.team.id === match.teamHomeId) {
                    playerByTeam.home.push(p.player.name)
                } else if (p.team.id === match.teamAwayId) {
                    playerByTeam.away.push(p.player.name)
                }
            })
            return {
                homeTeam: playerByTeam.home,
                awayTeam: playerByTeam.away
            }
        } catch (error) {
            console.log(error)
            logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            return {}
        }
    }

    async function fetchOddsPrediction(matchId) {
        try {
            const response = await fetchOdds(matchId)

            if (!response.data.response?.length) return;

            const bookmakers = response.data.response[0].bookmakers
            if (!Array.isArray(bookmakers) || bookmakers.length === 0) return;

            const selectedBookmaker =
                bookmakers.find(b => b.name === 'Bwin') || bookmakers[0];

            if (!selectedBookmaker) return;

            const matchWinnerBet = selectedBookmaker.bets.find(bet => bet.id === MATCH_WINNER_ID);
            const overUnderBet = selectedBookmaker.bets.find(bet => bet.id === OVER_UNDER_ID);

            const matchWinner = {
                homeWinOdds: null,
                drawOdds: null,
                awayWinOdds: null
            };

            matchWinnerBet?.values?.forEach(v => {
                if (v.value === "Home") matchWinner.homeWinOdds = v.odd;
                else if (v.value === "Draw") matchWinner.drawOdds = v.odd;
                else if (v.value === "Away") matchWinner.awayWinOdds = v.odd;
            });

            const overUnder2_5 = {
                over2_5Odds: null,
                under2_5Odds: null
            };

            overUnderBet?.values?.forEach(v => {
                if (v.value === "Over 2.5") overUnder2_5.over2_5Odds = v.odd;
                else if (v.value === "Under 2.5") overUnder2_5.under2_5Odds = v.odd;
            });
            return {
                matchWinner: matchWinner,
                overUnder2_5: overUnder2_5
            }
        } catch (error) {
            console.log(error)
            logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            return {}
        }
    }

    async function fetchTeamLineups(matchId) {
        try {
            const response = await fetchLineups(matchId)

            let homeLineup = [];
            let awayLineup = [];

            response.data.response.forEach(teamInfo => {
                const teamId = teamInfo.team.id;
                const lineup = teamInfo.startXI?.map(p => p.player.name) || [];

                if (teamId === match.teamHomeId) {
                    homeLineup = lineup;
                } else if (teamId === match.teamAwayId) {
                    awayLineup = lineup;
                }
            });

            return {
                homeLineup: homeLineup,
                awayLineup: awayLineup
            }
        } catch (error) {
            console.log('lineup error', error)
            logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            return {}
        }
    }

    async function generateSummary(homeTeamForm, awayTeamForm, playersNotPlaying, odds, lineups, h2h) {
        try {
            const matchData = {
                competition: match.leagueName,
                matchDate: match.matchDate,
                homeTeam: match.teamHomeName,
                awayTeam: match.teamAwayName
            }
            const homeTeam = {
                notPlayingPlayers: playersNotPlaying.homeTeam,
                last5MatchesForm: homeTeamForm.last5MatchesForm,
                averageGoalsScored: homeTeamForm.averageGoalsScored,
                averagesGoalsConceded: homeTeamForm.averagesGoalsConceded,
                likelyLineup: lineups.homeLineup
            }
            const awayTeam = {
                notPlayingPlayers: playersNotPlaying.awayTeam,
                last5MatchesForm: awayTeamForm.last5MatchesForm,
                averageGoalsScored: awayTeamForm.averageGoalsScored,
                averagesGoalsConceded: awayTeamForm.averagesGoalsConceded,
                likelyLineup: lineups.awayLineup
            }
            const response = await fetchSummary(homeTeam, awayTeam, odds, matchData, h2h, selectedLanguage)
            setSummary(response.data.summary)
            setLoading(false)
            setSaveDisabled(false)
            logEvent(analytics, 'summary_generated', {
                club_name_home: matchData.homeTeam,
                club_name_away: matchData.awayTeam,
                is_logged_in: !!authUser
            })
        } catch (error) {
            console.log(error)
            logEvent(analytics, 'summary_error', { error_message: error.message })
        }
    }

    const fetchHeadToHead = async (teamHomeId, teamAwayId) => {
        try {
            const response = await getHeadToHead(teamHomeId, teamAwayId)
            const h2h = response.data.response
            const headToHeadArray = []
            h2h.forEach(match => {
                headToHeadArray.push({
                    date: match.fixture.date,
                    league: match.league.name,
                    leagueSeason: match.league.season,
                    leagueRound: match.league.round,
                    homeTeam: match.teams.home.name,
                    awayTeam: match.teams.away.name,
                    result: `${match.goals.home}-${match.goals.away}`
                })
            })
            return headToHeadArray
        } catch (error) {
            console.log('Error fetching Head to head', error)
            logEvent(analytics, 'summary_details_fetch_error', { error_message_details_fails: error.message })
            return []
        }
    }

    const goToSearchPage = () => {
        navigate('/')
    }

    const handleSaveSummary = async () => {
        if (!authUser) {
            alert(t('onlyLoggedInCanSave'))
            return
        }
        setLoadingSummaryButton(true)
        setSaveDisabled(true)
        try {
            const summaryToSave = {
                matchId: `${match.id}`,
                summary: summary,
                date: match.matchDate,
                leagueName: match.leagueName,
                homeTeamName: match.teamHomeName,
                awayTeamName: match.teamAwayName,
                createdAt: serverTimestamp()
            }
            await setDoc(doc(db, "users", authUser.uid, "summaries", `${match.id}`), summaryToSave)
            setLoadingSummaryButton(false)
        } catch (error) {
            console.log(error)
            setLoadingSummaryButton(false)
            setSaveDisabled(false)
        }
    }

    return (
        <div className='search-page'>

            <div className="header-container">
                <div onClick={() => goToSearchPage()} className='header-left'>
                    <img src={logoImage} alt="Logo" className="logo" />
                    <h3>Sigurica AI</h3>
                </div>
                <div className='header-right'>
                    <Button onClick={handleSaveSummary} className="save-summary-button" disabled={saveDisabled}>
                        {loadingSummaryButton ? (
                            <div className="spinner-border spinner-border-sm-custom" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        ) : saveDisabled && !loading ? (
                            t('saved')
                        ) : (
                            t('save')
                        )}
                    </Button>
                </div>
            </div>

            <div className="aurora-container">
                <div className="aurora-background">
                    <Aurora
                        colorStops={["#3A29FF", "#FF94B4", "#FF3232"]}
                        blend={0.5}
                        amplitude={1.0}
                        speed={1}
                    />
                </div>
                <div className="overlay">
                    <h1 className="pb-2 result-page-title">{t('matchAnalysis')}</h1>
                    {loading ? (
                        <div className="loading-block">
                            <p className="loading-step">{loadingMessages[step]}</p>
                        </div>
                    ) : (
                        <div>
                            <div className="summary-block">
                                <p className="summary-subtitle">{t('aiSummary')}</p>
                                <p className="summary-text">{summary}</p>
                            </div>

                            <div className="details-section">

                                <div className="match-row">
                                    <div className="match-column">
                                        <h5>{formatTime(match.matchDate)}h</h5>
                                        <p className="details-section-date">{formatDate(match.matchDate)}</p>
                                        <div className="details-section-league">{match.leagueName}</div>
                                    </div>
                                </div>

                                <div className="teams-split">
                                    <div className="team-wrapper">
                                        <div className="team-header">
                                            <h5 className="team-name">{match.teamHomeName}</h5>
                                        </div>
                                        <div className="team-column">
                                            <p><strong className="me-1">{t('starting11')}</strong>
                                                {lineups.homeLineup && lineups.homeLineup.length > 0
                                                    ? lineups.homeLineup.join(', ')
                                                    : t('noInformation')}
                                            </p>
                                            <p><strong>{t('matchAbsentees')}</strong> {playersNotPlaying.homeTeam.join(', ')}</p>
                                            <p><strong>{t('last5MatchesForm')}</strong> {homeTeamData.last5MatchesForm}</p>
                                            <p><strong>{t('averageGoalsScored')}</strong> {homeTeamData.averageGoalsScored}</p>
                                            <p><strong>{t('averageGoalsConceded')}</strong> {homeTeamData.averagesGoalsConceded}</p>
                                        </div>
                                    </div>

                                    <div className="team-wrapper">
                                        <div className="team-header">
                                            <h5 className="team-name">{match.teamAwayName}</h5>
                                        </div>
                                        <div className="team-column">
                                            <p><strong className="me-1">{t('starting11')}</strong>
                                                {lineups.awayLineup && lineups.awayLineup.length > 0
                                                    ? lineups.awayLineup.join(', ')
                                                    : t('noInformation')}
                                            </p>
                                            <p><strong>{t('matchAbsentees')}</strong> {playersNotPlaying.awayTeam.join(', ')}</p>
                                            <p><strong>{t('last5MatchesForm')}</strong> {awayTeamData.last5MatchesForm}</p>
                                            <p><strong>{t('averageGoalsScored')}</strong> {awayTeamData.averageGoalsScored}</p>
                                            <p><strong>{t('averageGoalsConceded')}</strong> {awayTeamData.averagesGoalsConceded}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <FeedbackPopup />
                </div>
            </div>
        </div>

        // <div className="container-fluid matchesBackgroundColor">
        //     <div className='row d-flex justify-content-start'>
        //         <div className='col-xl-12 col-lg-12 col-md-12 col-sm-12 d-flex justify-content-start align-items-center'>
        //             <div className='d-flex align-items-center goToSearchPage'>
        //                 <i className="bi bi-arrow-left goBackIcon"></i>
        //                 <p className='goBackText'>Brain Bet</p>
        //             </div>
        //         </div>
        //     </div>

        //     {loading ? (
        //         <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        //             <div className="spinner-border" role="status">
        //                 <span className="visually-hidden">Loading...</span>
        //             </div>
        //         </div>
        //     ) : (
        //         <div className="row justify-content-center">
        //         </div>
        //     )}
        // </div>
    )
}

export function getLastMatchesForm(matches, teamId) {
    return matches.map(match => {
        const { home, away } = match.teams;

        const isHomeTeam = home.id === teamId;
        const team = isHomeTeam ? home : away;

        if (team.winner === true) return 'W';
        if (team.winner === false) return 'L';
        return 'D';
    }).join('');
}

export function calculateGoalAverages(matches, teamId) {
    let goalsScored = 0;
    let goalsConceded = 0;

    matches.forEach(match => {
        const isHome = match.teams.home.id === teamId;

        if (isHome) {
            goalsScored += match.goals.home;
            goalsConceded += match.goals.away;
        } else {
            goalsScored += match.goals.away;
            goalsConceded += match.goals.home;
        }
    });

    const matchCount = matches.length;
    const avgScored = matchCount > 0 ? (goalsScored / matchCount).toFixed(2) : 0;
    const avgConceded = matchCount > 0 ? (goalsConceded / matchCount).toFixed(2) : 0;

    return {
        averageGoalsScored: Number(avgScored),
        averagesGoalsConceded: Number(avgConceded)
    };
}
